import React, { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, Rocket, Plus, Clock, User, Check, Loader2, Wallet, X, Trash2 } from 'lucide-react';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { useAppContext } from '../hooks/useAppContext';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useTipWallet } from '../hooks/useTipWallet';
import { BOOST_KIND, BOOST_WALLET, POLL_BOOST_TAG, POLL_BOOST_TIERS, usdToSats } from '../lib/boost';
import { BCHLogo } from '../components/Layout';
import { useAccountMaturity } from '../hooks/useTrustScore';

const POLL_KIND = 1068;
const VOTE_KIND = 1018;

function timeLeft(closedAt: number): string {
  const secs = closedAt - Math.floor(Date.now() / 1000);
  if (secs <= 0) return 'Ended';
  if (secs < 3600) return `${Math.floor(secs / 60)}m left`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h left`;
  return `${Math.floor(secs / 86400)}d left`;
}

function getTimeAgo(ts: number): string {
  const secs = Math.floor(Date.now() / 1000) - ts;
  if (secs < 60) return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function PollsPage() {
  const { user } = useAppContext();
  const { publish } = useNostrPublish();
  const maturity = useAccountMaturity(user?.pubkey);
  const [filter, setFilter] = useState<'all' | 'active' | 'ended'>('all');
  const [showCreate, setShowCreate] = useState(false);
  const [boostingPoll, setBoostingPoll] = useState<any | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Poll composer state
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState(['', '']);
  const [duration, setDuration] = useState(24);
  const [publishing, setPublishing] = useState(false);
  const [publishError, setPublishError] = useState<string | null>(null);
  const [localPollBump, setLocalPollBump] = useState(0);
  const effectivePollRemaining = Math.max(0, maturity.postsRemainingToday - localPollBump);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handlePublishPoll = async () => {
    const validOptions = options.filter(o => o.trim());
    if (!question.trim()) { setPublishError('Question is required'); return; }
    if (validOptions.length < 2) { setPublishError('At least 2 options required'); return; }
    if (!maturity.isOperator && !maturity.isEstablished && effectivePollRemaining <= 0) {
      setPublishError('Posting limit reached for today. New accounts unlock more posts as they become established.');
      return;
    }
    setPublishing(true);
    setPublishError(null);
    try {
      const tags: string[][] = [];
      validOptions.forEach((opt, i) => tags.push(['option', String(i), opt.trim()]));
      tags.push(['polltype', 'single']);
      tags.push(['t', 'bch']);
      tags.push(['t', 'bchnostr']);
      if (duration > 0) tags.push(['closed_at', String(Math.floor(Date.now() / 1000) + duration * 3600)]);
      await publish({ kind: POLL_KIND, content: question.trim(), tags, created_at: Math.floor(Date.now() / 1000) });
      setLocalPollBump(prev => prev + 1);
      setQuestion('');
      setOptions(['', '']);
      setDuration(24);
      setShowCreate(false);
      showToast('✓ Poll published!');
    } catch (e) {
      setPublishError(e instanceof Error ? e.message : 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Fetch polls
  const { events: pollEvents, loading } = useNostrQuery({
    filters: [{ kinds: [POLL_KIND], '#t': ['bchnostr'], limit: 100 }],
  });

  // Fetch poll boosts
  const { events: boostEvents } = useNostrQuery({
    filters: [{ kinds: [BOOST_KIND], '#t': [POLL_BOOST_TAG], limit: 200 }],
  });

  // Build boosted poll id set
  const boostedIds = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    const ids = new Set<string>();
    boostEvents.forEach(ev => {
      try {
        const data = JSON.parse(ev.content);
        if (data.expiresAt > now) ids.add(data.eventId);
      } catch {}
    });
    return ids;
  }, [boostEvents]);

  // Filter + sort polls
  const polls = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return pollEvents
      .filter(ev => {
        const closedAt = ev.tags.find((t: string[]) => t[0] === 'closed_at')?.[1];
        const expired = closedAt ? parseInt(closedAt) < now : false;
        if (filter === 'active' && expired) return false;
        if (filter === 'ended' && !expired) return false;
        return true;
      })
      .sort((a, b) => {
        const aB = boostedIds.has(a.id);
        const bB = boostedIds.has(b.id);
        if (aB && !bB) return -1;
        if (!aB && bB) return 1;
        return b.created_at - a.created_at;
      });
  }, [pollEvents, filter, boostedIds]);

  return (
    <div>
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3">
        <div className="flex items-center justify-between mb-3">
          <h1 className="text-lg font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" /> Polls
          </h1>
          {user && (
            <button
              onClick={() => { setShowCreate(v => !v); setPublishError(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${showCreate ? 'bg-muted text-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
            >
              {showCreate ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {showCreate ? 'Cancel' : 'Create Poll'}
            </button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-1.5">
          {(['all', 'active', 'ended'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded-full text-xs font-medium capitalize transition-colors ${
                filter === f ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Inline Poll Composer */}
      {showCreate && (
        <div className="border-b border-border bg-muted/30 p-4 space-y-3">
          <textarea
            value={question}
            onChange={e => setQuestion(e.target.value)}
            placeholder="Ask a question..."
            rows={2}
            maxLength={280}
            className="w-full bg-background rounded-lg px-3 py-2.5 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary resize-none"
          />

          <div className="space-y-2">
            {options.map((opt, i) => (
              <div key={i} className="flex gap-2">
                <input
                  value={opt}
                  onChange={e => { const o = [...options]; o[i] = e.target.value; setOptions(o); }}
                  placeholder={`Option ${i + 1}`}
                  maxLength={100}
                  className="flex-1 bg-background rounded-lg px-3 py-2 text-sm border border-border focus:outline-none focus:ring-1 focus:ring-primary"
                />
                {options.length > 2 && (
                  <button onClick={() => setOptions(options.filter((_, idx) => idx !== i))} className="p-2 text-muted-foreground hover:text-red-500">
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <button onClick={() => setOptions([...options, ''])} className="text-xs text-primary hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add option
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground shrink-0">Duration:</label>
            <select
              value={duration}
              onChange={e => setDuration(Number(e.target.value))}
              className="bg-background rounded-lg px-2 py-1.5 text-xs border border-border focus:outline-none"
            >
              <option value={1}>1 hour</option>
              <option value={6}>6 hours</option>
              <option value={24}>24 hours</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
              <option value={0}>No expiry</option>
            </select>
          </div>

          {publishError && (
            <p className="text-xs text-red-500">{publishError}</p>
          )}

          <button
            onClick={handlePublishPoll}
            disabled={publishing || !question.trim()}
            className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
            {publishing ? 'Publishing...' : 'Publish Poll'}
          </button>
        </div>
      )}

      {/* List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-3 border-primary/20 border-t-primary rounded-full animate-spin" />
          </div>
        ) : polls.length === 0 ? (
          <div className="text-center py-16">
            <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground">No polls found.</p>
            {user && (
              <button onClick={() => setShowCreate(true)} className="text-sm text-primary hover:underline mt-2">
                Create the first poll!
              </button>
            )}
          </div>
        ) : (
          polls.map(ev => (
            <PollCard
              key={ev.id}
              event={ev}
              isBoosted={boostedIds.has(ev.id)}
              user={user}
              onBoost={() => setBoostingPoll(ev)}
            />
          ))
        )}
      </div>

      {/* Boost Modal */}
      {boostingPoll && (
        <PollBoostModal
          event={boostingPoll}
          onClose={() => setBoostingPoll(null)}
          onSuccess={() => { setBoostingPoll(null); showToast('🚀 Poll boosted!'); }}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-24 lg:bottom-8 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-medium shadow-lg flex items-center gap-2">
          <Check className="w-4 h-4 shrink-0" /> {toast}
        </div>
      )}
    </div>
  );
}

// ── Poll Card ──────────────────────────────────────────────────────
function PollCard({ event, isBoosted, user, onBoost }: {
  event: any; isBoosted: boolean; user: any; onBoost: () => void;
}) {
  const { profile } = useNostrProfile(event.pubkey);
  const { publish } = useNostrPublish();
  const [optimisticVote, setOptimisticVote] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const options = event.tags
    .filter((t: string[]) => t[0] === 'option')
    .map((t: string[]) => ({ id: t[1], label: t[2] }));

  const closedAt = event.tags.find((t: string[]) => t[0] === 'closed_at')?.[1];
  const isExpired = closedAt ? parseInt(closedAt) < Date.now() / 1000 : false;
  const isOwn = user && event.pubkey === user.pubkey;

  // Fetch votes
  const { events: voteEvents } = useNostrQuery({
    filters: [{ kinds: [VOTE_KIND], '#e': [event.id], limit: 500 }],
  });

  const { tally, myVotedOption } = useMemo(() => {
    const latest = new Map<string, any>();
    voteEvents.forEach((ev: any) => {
      const ex = latest.get(ev.pubkey);
      if (!ex || ev.created_at > ex.created_at) latest.set(ev.pubkey, ev);
    });
    const tally: Record<string, number> = {};
    let myVotedOption: string | null = null;
    latest.forEach((ev, pubkey) => {
      const r = ev.tags.find((t: string[]) => t[0] === 'response')?.[1];
      if (!r || !options.some((o: any) => o.id === r)) return;
      tally[r] = (tally[r] || 0) + 1;
      if (user && pubkey === user.pubkey) myVotedOption = r;
    });
    return { tally, myVotedOption };
  }, [voteEvents, user, options]);

  const displayTally = useMemo(() => {
    if (!optimisticVote || myVotedOption) return tally;
    return { ...tally, [optimisticVote]: (tally[optimisticVote] || 0) + 1 };
  }, [tally, optimisticVote, myVotedOption]);

  const voted = myVotedOption || optimisticVote;
  const totalVotes = Object.values(displayTally).reduce((a: number, b: number) => a + b, 0);
  const showBar = showResults || !!voted || isExpired;

  const handleVote = async (optionId: string) => {
    if (!user || voted || isExpired) return;
    setOptimisticVote(optionId);
    setShowResults(true);
    await publish({
      kind: VOTE_KIND,
      content: '',
      tags: [['e', event.id], ['p', event.pubkey], ['response', optionId]],
      created_at: Math.floor(Date.now() / 1000),
    });
  };

  return (
    <div className={`glass-card relative ${isBoosted ? 'border-amber-400/50 shadow-amber-400/10 shadow-md' : ''}`}>
      {isBoosted && (
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-amber-400 text-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-full">
          <Rocket className="w-2.5 h-2.5" /> Boosted
        </div>
      )}

      {/* Author */}
      <Link to={`/profile/${event.pubkey}`} className="flex items-center gap-2 mb-2">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-3.5 h-3.5 text-primary/60" />
          </div>
        )}
        <div className="min-w-0">
          <span className="text-xs font-semibold truncate">
            {profile?.display_name || profile?.name || event.pubkey.slice(0, 10) + '...'}
          </span>
          <span className="text-[10px] text-muted-foreground ml-1.5">{getTimeAgo(event.created_at)}</span>
        </div>
      </Link>

      {/* Question */}
      <p className="font-semibold text-sm mb-3 leading-snug pr-16">{event.content}</p>

      {/* Options */}
      <div className="space-y-2">
        {options.map((opt: any) => {
          const count = displayTally[opt.id] || 0;
          const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
          const isMyVote = voted === opt.id;
          return (
            <button
              key={opt.id}
              onClick={() => handleVote(opt.id)}
              disabled={!!voted || isExpired || !user}
              className={`relative w-full text-left px-3 py-2 rounded-lg border text-sm transition-all overflow-hidden ${
                isMyVote ? 'border-primary bg-primary/5'
                : voted || isExpired ? 'border-border opacity-80 cursor-default'
                : 'border-border hover:border-primary/50'
              }`}
            >
              {showBar && (
                <div
                  className={`absolute inset-y-0 left-0 transition-all duration-500 ${isMyVote ? 'bg-primary/15' : 'bg-muted'}`}
                  style={{ width: `${pct}%` }}
                />
              )}
              <span className="relative z-10 flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5">
                  {isMyVote && <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
                  {opt.label}
                </span>
                {showBar && <span className="text-xs text-muted-foreground font-mono shrink-0">{pct}%</span>}
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{totalVotes} vote{totalVotes !== 1 ? 's' : ''}</span>
          {closedAt && (
            <span className={`flex items-center gap-0.5 ${isExpired ? 'text-red-500' : 'text-emerald-500'}`}>
              <Clock className="w-3 h-3" />
              {timeLeft(parseInt(closedAt))}
            </span>
          )}
          {!showResults && !voted && !isExpired && (
            <button onClick={() => setShowResults(true)} className="text-primary hover:underline">
              Show results
            </button>
          )}
        </div>

        {/* Boost button — anyone can boost, not just owner */}
        {user && !isBoosted && (
          <button
            onClick={onBoost}
            className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400 border border-amber-400/40 px-2 py-0.5 rounded-full hover:bg-amber-400/10 transition-colors"
          >
            <Rocket className="w-3 h-3" /> Boost
          </button>
        )}
        {isBoosted && isOwn && (
          <span className="text-[11px] text-amber-500 flex items-center gap-1">
            <Rocket className="w-3 h-3" /> Boosted
          </span>
        )}
      </div>
    </div>
  );
}

// ── Poll Boost Modal ───────────────────────────────────────────────
function PollBoostModal({ event, onClose, onSuccess }: {
  event: any; onClose: () => void; onSuccess: () => void;
}) {
  const tipWallet = useTipWallet();
  const { price: bchPrice } = useBCHPrice();
  const { publish } = useNostrPublish();
  const [selectedTier, setSelectedTier] = useState(POLL_BOOST_TIERS[0].id);
  const [boosting, setBoosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inited, setInited] = useState(false);

  const tier = POLL_BOOST_TIERS.find(t => t.id === selectedTier)!;
  const tierSats = usdToSats(tier.priceUsd, bchPrice);
  const walletBalance = Number(tipWallet.balanceSats);
  const canAfford = tierSats !== null && walletBalance >= tierSats;

  const handleInit = async () => {
    setInited(true);
    await tipWallet.init().catch(() => {});
  };

  const handleBoost = async () => {
    if (!tierSats) return;
    setBoosting(true);
    setError(null);
    try {
      const txId = await tipWallet.sendBCH(BOOST_WALLET, BigInt(tierSats));
      const now = Math.floor(Date.now() / 1000);
      await publish({
        kind: BOOST_KIND,
        content: JSON.stringify({
          eventId: event.id,
          tier: tier.id,
          priceSats: tierSats,
          expiresAt: now + tier.duration,
          boostedBy: event.pubkey,
        }),
        tags: [
          ['d', `poll-boost-${event.id}-${now}`],
          ['t', POLL_BOOST_TAG],
          ['t', 'bch'],
          ['e', event.id],
          ['expires', String(now + tier.duration)],
          ['amount', String(tierSats)],
          ['txid', txId || ''],
        ],
        created_at: now,
      });
      tipWallet.refreshBalance();
      onSuccess();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Boost failed');
    } finally {
      setBoosting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm p-5 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-base flex items-center gap-2">
            <Rocket className="w-5 h-5 text-amber-500" /> Boost Poll
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted"><X className="w-4 h-4" /></button>
        </div>

        <p className="text-xs text-muted-foreground line-clamp-2">
          Boost: <span className="font-semibold text-foreground">"{event.content}"</span>
        </p>
        <p className="text-xs text-muted-foreground -mt-2">Pin this poll to the top of the Polls page for more visibility and votes.</p>

        {/* Tiers */}
        <div className="space-y-2">
          {POLL_BOOST_TIERS.map(t => (
            <button
              key={t.id}
              onClick={() => setSelectedTier(t.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all ${
                selectedTier === t.id ? 'border-amber-400 bg-amber-400/5 ring-1 ring-amber-400/30' : 'border-border hover:border-amber-400/40'
              }`}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-xl">{t.emoji}</span>
                <div className="text-left">
                  <p className="text-sm font-semibold">{t.label}</p>
                  <p className="text-[10px] text-muted-foreground">Pinned at top of Polls page</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-bold font-mono text-amber-600 dark:text-amber-400">${t.priceUsd.toFixed(2)}</p>
                <p className="text-[10px] text-muted-foreground font-mono">
                  {(() => { const s = usdToSats(t.priceUsd, bchPrice); return s ? `~${s.toLocaleString()} sats` : '...'; })()}
                </p>
              </div>
            </button>
          ))}
        </div>

        {/* Wallet */}
        {!tipWallet.ready ? (
          <div className="rounded-lg bg-muted/50 border border-border p-3 text-center">
            <p className="text-xs text-muted-foreground mb-2">Load your wallet to pay</p>
            <button
              onClick={handleInit}
              disabled={inited}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center gap-1.5 mx-auto"
            >
              {inited ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wallet className="w-3.5 h-3.5" />}
              {inited ? 'Loading...' : 'Load Wallet'}
            </button>
          </div>
        ) : (
          <div className="rounded-lg bg-muted/50 border border-border p-3 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">Wallet Balance</span>
            <span className={`text-xs font-mono font-bold ${canAfford ? 'text-emerald-500' : 'text-red-500'}`}>
              {bchPrice ? `$${((walletBalance / 1e8) * bchPrice).toFixed(2)}` : `${walletBalance.toLocaleString()} sats`}
            </span>
          </div>
        )}

        {error && <p className="text-xs text-red-500 text-center">{error}</p>}

        <button
          onClick={handleBoost}
          disabled={!tipWallet.ready || !canAfford || boosting}
          className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-gradient-to-r from-amber-400 to-amber-500 text-amber-900 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {boosting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
          {boosting ? 'Boosting...' : `Boost for $${tier.priceUsd.toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

